// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {CopyLinkSection} from '@app/features/app/components/dialogs/shared/CopyLinkSection';
import type {RecipientItem} from '@app/features/app/components/dialogs/shared/RecipientList';
import {RecipientList, useRecipientItems} from '@app/features/app/components/dialogs/shared/RecipientList';
import selectorStyles from '@app/features/app/components/dialogs/shared/SelectorModalStyles.module.css';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import styles from '@app/features/expressions/components/modals/GiftSendToFriendModal.module.css';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Input} from '@app/features/ui/components/form/Input';
import {useCopyLinkHandler} from '@app/lib/copy-link';
import * as SnowflakeUtils from '@app/vendor/snowflake/SnowflakeUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {MagnifyingGlassIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback, useState} from 'react';

const logger = new Logger('GiftSendToFriendModal');
interface GiftSendToFriendModalProps {
	code: string;
}
export const GiftSendToFriendModal = observer(function GiftSendToFriendModal({code}: GiftSendToFriendModalProps) {
	const {t} = useLingui();
	const [sentTo, setSentTo] = useState(new Map<string, boolean>());
	const [sendingTo, setSendingTo] = useState(new Set<string>());
	const recipients = useRecipientItems();
	const [searchQuery, setSearchQuery] = useState('');
	const giftUrl = `${RuntimeConfig.giftEndpoint}/${code}`;
	const handleCopy = useCopyLinkHandler(giftUrl, true);
	const handleSendGift = useCallback(
		async (item: RecipientItem) => {
			const userId = item.type === 'group_dm' ? item.id : item.user.id;
			setSendingTo((previous) => new Set(previous).add(userId));
			let targetChannelId: string;
			if (item.channelId) {
				targetChannelId = item.channelId;
			} else {
				targetChannelId = await PrivateChannelCommands.ensureDMChannel(item.user.id);
			}
			try {
				const result = await MessageCommands.send(targetChannelId, {
					content: `${t`I can't redeem this because I already have lifetime ${PREMIUM_PRODUCT_NAME}, so this gift is all yours!`}\n${giftUrl}`,
					nonce: SnowflakeUtils.fromTimestamp(Date.now()),
				});
				if (result) {
					setSentTo((previous) => new Map(previous).set(userId, true));
				}
			} catch (error) {
				logger.error('Failed to send gift link:', error);
				ToastCommands.error(t`Failed to send gift. Please try again.`);
			} finally {
				setSendingTo((previous) => {
					const next = new Set(previous);
					next.delete(userId);
					return next;
				});
			}
		},
		[giftUrl, t],
	);
	return (
		<Modal.Root size="small" centered>
			<Modal.Header title={t`You already have lifetime ${PREMIUM_PRODUCT_NAME}`}>
				<p className={styles.description}>
					<Trans>
						We'd love to give you more than infinity of {PREMIUM_PRODUCT_NAME}, but that would disrupt the space-time
						continuum. Instead, how about you send the gift to one of your friends?
					</Trans>
				</p>
				<div className={selectorStyles.headerSearch}>
					<Input
						value={searchQuery}
						onChange={(event) => setSearchQuery(event.target.value)}
						placeholder={t`Search friends`}
						leftIcon={<MagnifyingGlassIcon size={20} weight="bold" className={selectorStyles.searchIcon} />}
						className={selectorStyles.headerSearchInput}
					/>
				</div>
			</Modal.Header>
			<Modal.Content className={selectorStyles.selectorContent}>
				<RecipientList
					recipients={recipients}
					sendingTo={sendingTo}
					sentTo={sentTo}
					onSend={handleSendGift}
					defaultButtonLabel={t`Send Gift`}
					sentButtonLabel={t`Sent`}
					buttonClassName={styles.sendButton}
					scrollerKey="gift-send-to-friend-modal-friend-list-scroller"
					searchQuery={searchQuery}
					onSearchQueryChange={setSearchQuery}
					showSearchInput={false}
				/>
			</Modal.Content>
			<Modal.Footer>
				<CopyLinkSection
					label={<Trans>or send the gift link to a friend:</Trans>}
					value={giftUrl}
					onCopy={handleCopy}
					onInputClick={(event) => event.currentTarget.select()}
					inputProps={{placeholder: t`Gift link`}}
				>
					<p className={styles.giftCodeText}>
						<Trans>Gift Code:</Trans> <span className={styles.giftCode}>{code}</span>
					</p>
				</CopyLinkSection>
			</Modal.Footer>
		</Modal.Root>
	);
});
